
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface PopularLink {
  id: string;
  name: string;
  storeId: string;
  isActive: boolean;
}

interface StoreData {
  id: string;
  name: string;
  isActive: boolean;
}

interface SearchBarProps {
  onSearch: (query: string) => void;
}

export default function SearchBar({ onSearch }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const [popularLinks, setPopularLinks] = useState<PopularLink[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('popular_links');
    if (saved) {
      const links: PopularLink[] = JSON.parse(saved);
      setPopularLinks(links.filter(l => l.isActive));
    } else {
      // Дефолтные ссылки — привязываем к магазинам из admin_stores_v2
      const storesRaw = localStorage.getItem('admin_stores_v2');
      const stores: StoreData[] = storesRaw ? JSON.parse(storesRaw) : [];

      const defaultNames = ['AliExpress', 'Wildberries', 'Ozon', 'Lamoda', 'М.Видео', 'Booking.com'];
      const defaults: PopularLink[] = defaultNames.map((name, i) => {
        const found = stores.find(s => s.name.toLowerCase() === name.toLowerCase());
        return {
          id: `pl-${i}`,
          name,
          storeId: found ? found.id : '',
          isActive: true,
        };
      });
      setPopularLinks(defaults);
      localStorage.setItem('popular_links', JSON.stringify(defaults));
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
    }
  };

  const getStoreSlug = (storeName: string): string => {
    return storeName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9а-яё\-]/gi, '')
      .replace(/\.ru$/, '')
      .replace(/\.com$/, '');
  };

  const handleLinkClick = (link: PopularLink) => {
    if (link.storeId) {
      // Ищем магазин по id чтобы получить slug
      const storesRaw = localStorage.getItem('admin_stores_v2');
      if (storesRaw) {
        const stores: StoreData[] = JSON.parse(storesRaw);
        const store = stores.find(s => s.id === link.storeId && s.isActive);
        if (store) {
          navigate(`/store/${getStoreSlug(store.name)}`);
          return;
        }
      }
    }
    // Если магазин не найден — делаем поиск
    onSearch(link.name);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="relative mb-6">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по магазинам, брендам или категориям..."
            className="w-full px-6 py-4 text-lg border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent shadow-lg"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white px-6 py-2 rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line mr-2"></i>
            Найти
          </button>
        </div>
      </form>

      {popularLinks.length > 0 && (
        <div className="text-center">
          <p className="text-gray-600 mb-3">Популярные поиски:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {popularLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleLinkClick(link)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-orange-400 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
